<template>
  <!-- persistent + no close affordance: this is a gate, not a notice. There is
       no scrim click, no Escape and no ✕, because every one of them is a way to
       start recording doses against a protocol nobody chose. -->
  <v-dialog
    v-model="open"
    persistent
    :max-width="640"
    :scrim="'rgba(17, 24, 39, 0.5)'"
    role="dialog"
    aria-modal="true"
  >
    <v-card rounded="xl" elevation="24" class="setup-card">
      <ClinicalCardHeader
        :title="$t('userAccount.clinical.title')"
        :jurisdiction="jurisdiction"
        class="px-6 py-2"
      >
        <template #actions>
          <!-- Still a real link with an href, not a click handler — the url is
               the point, so it has to be copyable and openable in a new tab the
               way any other link is. -->
          <v-btn
            :href="disclaimerUrl"
            target="_blank"
            rel="noopener"
            variant="text"
            size="small"
            rounded="xl"
            class="header-disclaimer"
            :title="$t('userAccount.clinical.disclaimer.link')"
          >
            {{ $t('userAccount.clinical.disclaimer.link') }}
          </v-btn>

          <!-- Whose protocol this is and the document behind it. Behind the
               glyph rather than printed: read once and skipped forever after,
               while permanently pushing the limits themselves down the card.

               On the bar, not beside the protocol's name, because it answers
               for the whole of what this dialog proposes — and because the
               settings card a pharmacist meets next puts the same two
               controls in the same corner. -->
          <v-menu
            v-model="citationOpen"
            open-on-hover
            open-on-click
            location="bottom end"
            :close-on-content-click="false"
            :open-delay="120"
            :close-delay="180"
          >
            <template #activator="{ props: menuProps }">
              <v-btn
                v-bind="menuProps"
                icon
                variant="text"
                size="small"
                class="info-open"
                :aria-label="$t('userAccount.clinical.citation.open')"
              >
                <v-icon size="18">fa-regular fa-circle-info</v-icon>
              </v-btn>
            </template>

            <v-card rounded="lg" elevation="4" class="citation-card pa-4">
              <template v-if="selectedProtocol">
                <p class="text-subtitle-2 mb-1">{{ protocolLabel(selectedProtocol) }}</p>
                <p v-if="!selectedProtocol.isBaseline" class="text-caption mb-1">
                  {{ $t('userAccount.clinical.publishedBy', { publisher: selectedProtocol.publisher }) }}
                </p>
                <p v-if="selectedProtocol.reference" class="text-caption text-medium-emphasis mb-1">
                  {{ selectedProtocol.reference }}
                </p>
                <p v-if="selectedProtocol.isBaseline" class="text-caption text-medium-emphasis mb-1">
                  {{ $t('userAccount.clinical.baseline.note') }}
                </p>
                <!-- The registry's `source.note` is NOT shown here. On a
                     baseline it restates the line above in the negative
                     ("applied where no protocol has been written… it is
                     NOT clinical guidance"), and this panel describes what
                     the protocol is, not what it is not. It stays on
                     Settings → Clinique, where it sits beside the
                     per-field citations it qualifies. -->
              </template>

              <!-- Outside the protocol block, because it is true whether or
                   not one has resolved: nothing on this dialog is a decision
                   a pharmacy is stuck with. Said here rather than under the
                   button — a reader who opened this panel is the one asking
                   whose numbers these are, and "and you can change them" is
                   the other half of that answer. -->
              <p class="text-caption text-medium-emphasis mt-2 mb-0">
                {{ $t('userAccount.clinicalSetup.adjustable') }}
              </p>
            </v-card>
          </v-menu>
        </template>
      </ClinicalCardHeader>

      <div class="setup-body px-6 py-5">
        <!-- Nothing loaded, so there is no choice to offer: the two selects
             would be empty and Confirm would be dead. -->
        <BaseAlert v-if="loadFailed" type="error" class="mb-2">
          <strong>{{ $t('userAccount.clinical.loadFailed.title') }}</strong><br>
          {{
            errorStatus === 401
              ? $t('userAccount.clinical.loadFailed.signedOut')
              : $t('userAccount.clinical.loadFailed.message')
          }}
          <div class="text-caption text-medium-emphasis mt-1">
            {{ $t('userAccount.clinical.loadFailed.detail', { detail: error }) }}
          </div>
          <v-btn
            class="mt-3"
            color="#2563eb"
            variant="flat"
            rounded="xl"
            elevation="0"
            size="small"
            :loading="loading"
            @click="retryLoad"
          >
            {{ $t('userAccount.clinical.loadFailed.retry') }}
          </v-btn>
        </BaseAlert>

        <template v-else>
          <!-- ── What is in force, and the way to change it ──────────────────
               The portal's protocol panel opened on exactly this: the name a
               pharmacist would quote and the molecule under it. That is the
               identity of the numbers below, and it is what a screenshot of
               this dialog has to carry on its own — two permanently-open
               dropdowns say "fill in this form" instead.

               The pen is this screen's own addition: revealed on hover or
               keyboard focus, but always occupying its space, so the line does
               not shift as the pointer crosses it. -->
          <div class="identity">
            <!-- ── The protocol, in place ─────────────────────────────────
                 Read state is the name a pharmacist would quote; whose it is
                 is on the bar above. Its own pen turns THIS line into the
                 select — not a form that opens underneath it, because the thing
                 being changed and the control that changes it should be the
                 same piece of the screen. Same idiom as the limit rows on
                 Settings → Clinique. -->
            <div class="identity-line d-flex align-center ga-2">
              <template v-if="editingField !== 'protocol'">
                <span class="text-body-1 font-weight-medium">{{ protocolLabel(selectedProtocol) }}</span>

                <v-btn
                  variant="text"
                  size="x-small"
                  class="identity-pen"
                  :title="$t('userAccount.clinicalSetup.change')"
                  :aria-label="$t('userAccount.clinicalSetup.change')"
                  @click="startEdit('protocol')"
                >
                  <!-- Inline SVG, as on the settings screen: both Font Awesome
                       sources in this app are SUBSETS, and a pen glyph missing
                       from whichever was subsetted last renders as a dashed
                       placeholder. These two draw themselves. -->
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M17 3a2.828 2.828 0 0 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                  </svg>
                </v-btn>
              </template>

              <template v-else>
                <v-select
                  v-model="selectedProtocolId"
                  :items="protocolItems"
                  item-title="title"
                  item-value="value"
                  :aria-label="$t('userAccount.clinical.protocol')"
                  variant="underlined"
                  density="compact"
                  hide-details
                  autofocus
                  class="identity-field flex-grow-1"
                  @update:model-value="closeEdit"
                />
                <v-btn
                  variant="text"
                  size="x-small"
                  class="identity-check"
                  :title="$t('userAccount.clinicalSetup.done')"
                  :aria-label="$t('userAccount.clinicalSetup.done')"
                  @click="closeEdit"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </v-btn>
              </template>
            </div>

            <!-- ── The molecule, likewise ─────────────────────────────────
                 Its own line and its own pen. Changing it re-bases the protocol
                 above (see `defaultProtocolFor`), which is why it cannot simply
                 be folded into the same control. -->
            <div class="identity-line identity-molecule d-flex align-center ga-2">
              <template v-if="editingField !== 'molecule'">
                <span class="text-caption text-medium-emphasis">{{ moleculeLine }}</span>
                <v-btn
                  variant="text"
                  size="x-small"
                  class="identity-pen"
                  :title="$t('userAccount.clinical.molecule')"
                  :aria-label="$t('userAccount.clinical.molecule')"
                  @click="startEdit('molecule')"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M17 3a2.828 2.828 0 0 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                  </svg>
                </v-btn>
              </template>

              <template v-else>
                <v-select
                  v-model="selectedMoleculeId"
                  :items="moleculeItems"
                  item-title="title"
                  item-value="value"
                  :aria-label="$t('userAccount.clinical.molecule')"
                  variant="underlined"
                  density="compact"
                  hide-details
                  autofocus
                  class="identity-field flex-grow-1"
                  @update:model-value="closeEdit"
                />
                <v-btn
                  variant="text"
                  size="x-small"
                  class="identity-check"
                  :title="$t('userAccount.clinicalSetup.done')"
                  :aria-label="$t('userAccount.clinicalSetup.done')"
                  @click="closeEdit"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </v-btn>
              </template>
            </div>
          </div>

          <!-- ── The numbers the name above commits the pharmacy to ─────────
               Each row edits in place on its own pen, exactly like the protocol
               line: the value cell becomes the input, and a justification is
               asked for underneath it. A limit a pharmacy moves is that
               pharmacy's own statement, so nothing is stored without a reason.

               Staged, not written. The whole set goes in on Confirm, AFTER the
               protocol lands — the server refuses to layer an override onto a
               protocol nobody selected, so the order is load-bearing. -->
          <template v-if="rows.length">
            <div class="d-flex align-center ga-2 mt-5">
              <p class="setup-limits-label text-caption text-medium-emphasis mb-0">
                {{ $t('userAccount.clinicalSetup.limits') }}
              </p>
              <v-spacer />
              <span v-if="adjustedCount" class="adjusted-count text-caption">
                {{ $t('userAccount.clinical.adjust.count', { n: adjustedCount }) }}
              </span>
            </div>

            <ul class="setup-limits">
              <li v-for="row in rows" :key="row.field" class="limit-row px-6">
                <div class="d-flex align-center ga-3">
                  <span class="text-body-2 flex-grow-1">
                    {{ fieldLabel(row.field) }}
                    <span v-if="row.hardCap && editingLimit === row.field" class="blocks-chip">
                      {{ $t('userAccount.clinical.adjust.blocks') }}
                    </span>
                  </span>

                  <template v-if="editingLimit !== row.field">
                    <!-- The number is its own way in. A reader decides a
                         limit should be different while looking AT the limit, and
                         making them travel to a 14px glyph to say so taxes the
                         one thing this screen is for. The pen stays, because it
                         is what NAMES the affordance for someone who has not
                         thought to click a number.

                         Redundant to the pen, so it is hidden from the tab order
                         and from a screen reader: two stops per row, both saying
                         "edit this row", is sixteen extra stops on a card that
                         has one already. -->
                    <button
                      v-if="canAdjust(row.field)"
                      type="button"
                      tabindex="-1"
                      aria-hidden="true"
                      class="text-body-2 font-weight-medium limit-value limit-value-btn"
                      :class="{ 'limit-value-changed': row.differs }"
                      :title="$t('userAccount.clinical.adjust.edit')"
                      @click="startLimitEdit(row)"
                    >
                      {{ displayValue(row.value, unitFor(row)) }}
                    </button>
                    <span
                      v-else
                      class="text-body-2 font-weight-medium limit-value"
                      :class="{ 'limit-value-changed': row.differs }"
                    >
                      {{ displayValue(row.value, unitFor(row)) }}
                    </span>
                    <v-btn
                      v-if="canAdjust(row.field)"
                      variant="text"
                      size="x-small"
                      class="limit-pen"
                      :title="$t('userAccount.clinical.adjust.edit')"
                      :aria-label="`${$t('userAccount.clinical.adjust.edit')}: ${fieldLabel(row.field)}`"
                      @click="startLimitEdit(row)"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M17 3a2.828 2.828 0 0 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                      </svg>
                    </v-btn>
                    <!-- Holds the pen's width on a row that has none, so the
                         values stay in one column. -->
                    <span v-else class="pen-spacer" aria-hidden="true"></span>
                  </template>

                  <template v-else>
                    <!-- Some limits are a flag rather than a number
                         ("warn on a week of identical doses"). A number field
                         bound to one would write 0/1 into a boolean the server
                         validates by type. -->
                    <v-checkbox
                      v-if="row.type === 'boolean'"
                      v-model="draft[row.field].value"
                      :aria-label="fieldLabel(row.field)"
                      density="compact"
                      hide-details
                      class="limit-toggle"
                    />
                    <v-text-field
                      v-else
                      v-model="draft[row.field].value"
                      type="number"
                      :suffix="unitFor(row)"
                      :aria-label="fieldLabel(row.field)"
                      variant="underlined"
                      density="compact"
                      hide-details
                      autofocus
                      class="limit-field"
                      @keyup.enter="confirmLimit(row)"
                    />
                    <v-btn
                      variant="text"
                      size="x-small"
                      class="identity-check"
                      :title="$t('userAccount.clinical.adjust.confirm')"
                      :aria-label="$t('userAccount.clinical.adjust.confirm')"
                      @click="confirmLimit(row)"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    </v-btn>
                  </template>
                </div>

                <!-- What the protocol itself says, once this row stops saying
                     it. Never a footnote on an unchanged row. -->
                <p
                  v-if="row.differs && editingLimit !== row.field"
                  class="text-caption text-medium-emphasis mt-n1 mb-1"
                >
                  {{ $t('userAccount.clinical.adjust.protocolValue', {
                    value: displayValue(row.base, unitFor(row))
                  }) }}
                </p>

                <!-- A limit this pharmacy moves is this pharmacy's statement,
                     and the statement is part of the clinical record. The server
                     rejects a set without one, so it is asked for here rather
                     than reported as a failure after Confirm. -->
                <div v-if="editingLimit === row.field" class="pb-2">
                  <v-text-field
                    v-model="draft[row.field].reason"
                    :label="$t('userAccount.clinical.adjust.reason')"
                    :placeholder="$t('userAccount.clinical.adjust.reasonHint')"
                    :error-messages="reasonMissing ? $t('userAccount.clinical.adjust.reasonRequired') : ''"
                    variant="underlined"
                    density="compact"
                    :counter="500"
                    maxlength="500"
                    class="mt-1"
                    @keyup.enter="confirmLimit(row)"
                  />
                  <div class="d-flex justify-end">
                    <v-btn variant="text" size="x-small" @click="revertLimit(row.field)">
                      {{ $t('userAccount.clinical.adjust.revert') }}
                    </v-btn>
                  </div>
                </div>
              </li>
            </ul>

            <!-- ── The adjustment ladder ────────────────────────────────────
                 Not adjustable, and not a ceiling: it is how the protocol
                 expects a dose change to be sized once a result is in. A
                 pharmacy pressing "Use this protocol" is accepting this too,
                 so it belongs on the dialog that asks them to — the same
                 block, in the same words, as the settings card they will meet
                 next. Null is a real state and is rendered as one: that check
                 simply does not run for this account. -->
            <h4 class="section-heading mt-6">
              {{ $t('userAccount.clinical.directional.title') }}
            </h4>
            <p v-if="!directional" class="text-body-2 text-medium-emphasis mt-2 mb-0">
              {{ $t('userAccount.clinical.directional.none') }}
            </p>
            <ul v-else class="ladder-list text-body-2 mt-2">
              <li v-for="(threshold, i) in directional.suspendThresholds" :key="i">
                {{ $t('userAccount.clinical.directional.suspend', {
                  test: testUnit,
                  inr: threshold.inr,
                  min: threshold.targetMin,
                  max: threshold.targetMax
                }) }}
              </li>
              <li>
                {{ $t('userAccount.clinical.directional.suspendOther', {
                  delta: directional.suspendThresholdOverTargetMax
                }) }}
              </li>
              <li>
                {{ $t('userAccount.clinical.directional.minIncrease', {
                  percent: directional.minIncreasePercentWhenFarBelow
                }) }}
              </li>
              <li>
                {{ $t('userAccount.clinical.directional.minDecrease', {
                  percent: directional.minDecreasePercentWhenFarAbove
                }) }}
              </li>
            </ul>

          </template>
        </template>
      </div>

      <!-- ── One row of actions ─────────────────────────────────────────────
           The two that change what is ON the card are outlined and sit left;
           the one that commits it is solid and sits right, sized to its own
           label like the rest of the row — filling the leftover width made a
           wide dialog's Confirm read as a pale bar with the label lost in it.

           Reset is disabled rather than hidden when nothing is adjusted: a row
           of buttons that changes its own shape as you work in the list above
           makes the commit button move under the pointer. -->
      <div v-if="!loadFailed" class="setup-footer d-flex align-center ga-2 px-6 py-4">
        <v-btn
          v-if="hiddenCount || expanded"
          variant="outlined"
          rounded="xl"
          size="small"
          class="footer-action"
          @click="expanded = !expanded"
        >
          {{ expanded
            ? $t('userAccount.clinical.adjust.showLess')
            : $t('userAccount.clinical.adjust.showAll') }}
        </v-btn>

        <v-btn
          variant="outlined"
          rounded="xl"
          size="small"
          class="footer-action"
          :disabled="!adjustedCount"
          @click="resetDraft"
        >
          {{ $t('userAccount.clinical.adjust.resetAll') }}
        </v-btn>

        <!-- The screen that ASKS a pharmacy to accept a set of limits could
             not show who wrote them: the receipts lived only on Settings →
             Clinique, behind a fold, on the screen you reach AFTER agreeing.
             Same button, same list, same component as there. -->
        <ClinicalSourcesButton
          :sources="sourceList"
          :text="sourcesText"
          :kind-label="kindLabel"
        />

        <v-btn
          color="#2563eb"
          variant="flat"
          rounded="xl"
          elevation="0"
          size="small"
          class="footer-action ml-auto"
          :disabled="!selectedProtocolId"
          :loading="saving"
          @click="confirm"
        >
          {{ $t('userAccount.clinicalSetup.confirm') }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
/**
 * The first thing an account owner sees in Retroact: which protocol are your
 * doses checked against?
 *
 * ── Why this exists, and why it is a modal ──────────────────────────────────
 * The question used to be asked during Praxio signup, as an echo under the
 * country picker with the ceilings folded behind it. It moved because Praxio is
 * a platform and Retroact is an app: the protocol decides what THIS product
 * will refuse to record, and the person accepting that should be looking at the
 * numbers, in the product that enforces them.
 *
 * It is a persistent dialog rather than a route, so it is presented DIRECTLY on
 * first login — over whatever the app was about to show — instead of depending
 * on a redirect to arrive somewhere else first. The blue chrome is the portal's
 * old protocol panel, which is the shape this question has always had.
 *
 * ── Who sees it ─────────────────────────────────────────────────────────────
 * `setupRequired` is the server's answer and this component never second-guesses
 * it (routes/clinical.routes.js): the owner of an account that has chosen no
 * protocol AND is new — either carrying Praxio's setup marker or having no
 * patients at all. An established pharmacy is never interrupted, and an invited
 * member is never asked a question only the owner can answer.
 *
 * ── What it deliberately does NOT do ────────────────────────────────────────
 * Adjust a limit. Every ceiling here can be changed, but changing one means
 * recording a justification against a protocol that is not in force yet, and
 * the server refuses to layer an override onto a protocol nobody selected. So
 * this makes the selection and Settings → Clinique does the rest.
 */
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseAlert from '../common/BaseAlert.vue'
import ClinicalCardHeader from './ClinicalCardHeader.vue'
import ClinicalSourcesButton from './ClinicalSourcesButton.vue'
import { showNotification } from '../../stores/notification'
import { useClinicalProtocol } from '../composables/useClinicalProtocol.js'
import { useDisclaimerUrl } from '../composables/useDisclaimerUrl.js'
import { useClinicalSources } from '../composables/useClinicalSources.js'
import {
  fieldLabel as labelForField,
  unitFor as unitForRow,
  displayValue as readValue,
  headlineRows,
  publishedValue
} from '../composables/clinicalFields.js'

const { t } = useI18n()

const {
  catalogue,
  profile,
  loading,
  error,
  errorStatus,
  loaded,
  setupRequired,
  jurisdiction,
  overridable,
  load,
  save,
  saveOverrides
} = useClinicalProtocol()

const selectedMoleculeId = ref(null)
const selectedProtocolId = ref(null)
const saving = ref(false)
const citationOpen = ref(false)

/** Named in the header, beside the panel that says whose protocol this is. */
const disclaimerUrl = useDisclaimerUrl()

/** The adjustment ladder, or null when the protocol tabulates none. */
const directional = computed(() => selectedProtocol.value?.directional ?? null)

const testUnit = computed(() => t('userAccount.clinical.testUnit'))

/**
 * Which identity line is currently a select instead of text: 'protocol',
 * 'molecule', or null.
 *
 * Closed by default, always: on a first run the proposal is already correct for
 * almost every pharmacy, and opening on a form asks a question that has already
 * been answered.
 *
 * One field at a time, because they are one decision seen from two sides —
 * changing the molecule re-bases the protocol above it, and two open selects
 * would show that happening in a field the reader is not looking at.
 */
const editingField = ref(null)

function startEdit(field) {
  editingField.value = field
}

/**
 * Close the open line.
 *
 * Also bound to the select's own change event: choosing a value IS the
 * confirmation, so the row collapses back to the name the moment there is a new
 * one to show. The check beside it is for leaving without choosing.
 */
function closeEdit() {
  editingField.value = null
}

/**
 * Open exactly when the server says this owner still has to choose.
 *
 * A computed with a no-op setter rather than a plain ref: `v-dialog` writes to
 * its model on the paths that would normally dismiss it, and swallowing those
 * writes is what makes the gate hold even if a future Vuetify adds one. It
 * closes only when `setupRequired` turns false, which only a successful save
 * does.
 */
const open = computed({
  get: () => setupRequired.value || saving.value,
  set: () => {}
})

const loadFailed = computed(() => !loaded.value && Boolean(error.value))

const molecules = computed(() => catalogue.value?.molecules ?? [])

const moleculeItems = computed(() =>
  molecules.value.map((m) => ({ title: m.inn, value: m.id }))
)

const selectedMolecule = computed(
  () => molecules.value.find((m) => m.id === selectedMoleculeId.value) ?? null
)

/**
 * A baseline's `name` is built from a molecule id ("Baseline — phenprocoumon"),
 * which is a key rather than copy; an authored protocol's is its publisher's
 * proper noun and is rendered untranslated. Same rule as the settings screen.
 */
function protocolLabel(protocol) {
  if (!protocol) return ''
  return protocol.isBaseline ? t('userAccount.clinical.baseline.name') : protocol.name
}

/**
 * Which protocols this account may choose from — the molecule's baseline, plus
 * whatever was written for the country the pharmacy practises in.
 *
 * Not every protocol for the molecule: a German account offered "FNT —
 * Nederland" is being shown the only phenprocoumon guideline that exists
 * anywhere, written for a country it does not practise in. The settings screen
 * filters identically.
 */
const selectableProtocols = computed(() => {
  const all = selectedMolecule.value?.protocols ?? []
  const country = jurisdiction.value?.code
  if (!country) return all
  return all.filter((protocol) =>
    protocol.isBaseline
    || protocol.id === selectedProtocolId.value
    // 'CA-QC' is Quebec's protocol and Canada's country. '*' is a baseline.
    || String(protocol.region ?? '').split('-')[0] === country
  )
})

const protocolItems = computed(() =>
  selectableProtocols.value.map((p) => ({ title: protocolLabel(p), value: p.id }))
)

const selectedProtocol = computed(
  () => selectedMolecule.value?.protocols.find((p) => p.id === selectedProtocolId.value) ?? null
)

/** The molecule and its brands, worded as the settings screen words them. */
const moleculeLine = computed(() => {
  const molecule = selectedMolecule.value ?? profile.value?.molecule
  if (!molecule?.inn) return ''
  return t('userAccount.clinical.moleculeLine', {
    molecule: molecule.inn,
    brands: (molecule.brandExamples ?? []).join(', ')
  })
})

const fieldLabel = (field) => labelForField(t, field)
const unitFor = (row) => unitForRow(t, row)
const displayValue = (value, unit) => readValue(t, value, unit)

/**
 * The headline limits the SELECTED protocol publishes.
 *
 * Read off the catalogue rather than off the profile, because nothing has been
 * saved yet: the numbers have to follow the dropdown, or the list would show
 * what is currently resolved while the owner reads a different protocol's name
 * above it.
 */
/**
 * Adjustments staged on this screen: `field -> { value, reason, active }`.
 *
 * Nothing here is written until Confirm. `active` is tracked separately from
 * "the value differs from the protocol" on purpose: a pharmacy may deliberately
 * pin a number the protocol currently agrees with, so that a later edition of
 * the guideline cannot move it silently. Deriving `active` from a comparison
 * would discard exactly that decision.
 */
const draft = ref({})

/** The row currently showing an input instead of its value, or null. */
const editingLimit = ref(null)

/** Set while the open row has a value but no justification yet. */
const reasonMissing = ref(false)

/**
 * Which fields the server will actually accept an override for, from the
 * profile. Never a local list: a screen that offers a field the server rejects
 * turns a considered adjustment into an error message after the fact.
 */
const adjustableFields = computed(() => new Set(overridable.value.map((f) => f.field)))
const canAdjust = (field) => adjustableFields.value.has(field)

/** Everything back to the protocol's own numbers — staged, not written. */
function resetDraft() {
  draft.value = {}
  editingLimit.value = null
  reasonMissing.value = false
}

/** Is the whole list showing, or only the headline limits? */
const expanded = ref(false)

/**
 * Every limit this protocol enforces, headline ones first.
 *
 * The union of two lists, and it has to be. `headlineRows` is what the SELECTED
 * protocol publishes — read off the catalogue, so the card follows the dropdown
 * rather than lagging on whatever is currently resolved. `overridable` is the
 * server's list of what may be ADJUSTED, which is longer and which the client
 * must never invent. A field in the first and not the second is shown without a
 * pen; a field in the second only is one of the ten the short view folds away.
 */
const allRows = computed(() => {
  const protocol = selectedProtocol.value
  if (!protocol) return []
  const doseUnit = selectedMolecule.value?.doseUnit ?? 'mg'

  const headline = headlineRows(protocol, doseUnit)
  const seen = new Set(headline.map((row) => row.field))
  const rest = overridable.value
    .filter((f) => !seen.has(f.field))
    .map((f) => {
      // The protocol's own number for the field, off the SELECTION. `f.base` is
      // the resolved protocol's, which is a different document the moment a
      // pharmacist picks something else in the line above.
      const published = publishedValue(protocol, f.field)
      return {
        ...f,
        base: published === undefined ? f.base : published,
        headline: false
      }
    })

  return [...headline, ...rest].map((row) => {
    const entry = draft.value[row.field]
    const active = entry?.active === true
    const value = active ? entry.value : row.base
    return {
      ...row,
      value,
      active,
      // An override may deliberately pin a value the protocol already agrees
      // with. That is still an override — it is just not a different number.
      differs: active && !sameValue(value, row.base),
      hardCap: overridable.value.find((f) => f.field === row.field)?.hardCap === true
    }
  })
})

/**
 * What is on screen: the headline limits, plus any the pharmacy has adjusted.
 *
 * An adjusted limit is never folded away. A number this account set, hidden
 * behind "show all", is exactly the one a colleague needs to see without being
 * told to look for it.
 */
const rows = computed(() =>
  expanded.value ? allRows.value : allRows.value.filter((r) => r.headline || r.active)
)

const hiddenCount = computed(() => allRows.value.length - rows.value.length)

const adjustedCount = computed(() => rows.value.filter((r) => r.active).length)

const { sourceList, kindLabel, sourcesText } = useClinicalSources(
  selectedProtocol,
  allRows
)

/** The set to send, in the shape `PUT /clinical/overrides` expects. */
const stagedOverrides = computed(() =>
  Object.fromEntries(
    Object.entries(draft.value)
      .filter(([, entry]) => entry.active)
      .map(([field, entry]) => [
        field,
        {
          value: typeof entry.value === 'boolean' ? entry.value : Number(entry.value),
          reason: entry.reason
        }
      ])
  )
)

function startLimitEdit(row) {
  reasonMissing.value = false
  if (!draft.value[row.field]) {
    draft.value[row.field] = { value: row.base, reason: '', active: false }
  }
  editingLimit.value = row.field
}

/** A flag compares by identity; a number may arrive as a string from an input. */
function sameValue(a, b) {
  if (typeof a === 'boolean' || typeof b === 'boolean') return a === b
  return Number(a) === Number(b)
}

/**
 * Close the open row, keeping what it holds.
 *
 * A value back at the protocol's number with no reason given is a revert, not
 * an adjustment — otherwise opening a row and closing it again would record an
 * override that changes nothing and costs an audit line.
 */
function confirmLimit(row) {
  const entry = draft.value[row.field]
  if (!entry) {
    editingLimit.value = null
    return
  }
  const unchanged = sameValue(entry.value, row.base)
  const reason = String(entry.reason ?? '').trim()
  if (unchanged && !reason) {
    revertLimit(row.field)
    return
  }
  if (!reason) {
    reasonMissing.value = true
    return
  }
  entry.reason = reason
  entry.active = true
  reasonMissing.value = false
  editingLimit.value = null
}

/** This limit back to the protocol's own number. */
function revertLimit(field) {
  delete draft.value[field]
  reasonMissing.value = false
  editingLimit.value = null
}

/**
 * A different protocol — or a different molecule under it — is a different
 * basis, and an adjustment argued against one basis must never be carried onto
 * another. Across molecules that is not merely wrong: 20 mg of warfarin is a
 * high dose, 20 mg of acenocoumarol is roughly five times any labelled
 * maintenance dose. The server drops a stored set on a protocol change for the
 * same reason; this drops the staged one.
 */
watch([selectedProtocolId, selectedMoleculeId], resetDraft)

/**
 * The protocol a molecule should open on.
 *
 * In order: one written for the country this pharmacy practises in, then the
 * molecule's baseline, then whatever is first. There is always an answer, which
 * is the point — see the watcher below.
 */
function defaultProtocolFor(molecule) {
  const protocols = molecule?.protocols ?? []
  if (protocols.length === 0) return null
  const country = jurisdiction.value?.code
  // 'CA-QC' is Quebec's protocol and Canada's country. '*' is a baseline.
  const local = country
    ? protocols.find((p) => String(p.region ?? '').split('-')[0] === country)
    : null
  return (local ?? protocols.find((p) => p.isBaseline) ?? protocols[0]).id
}

/**
 * Changing the molecule invalidates the protocol beneath it, and a protocol is
 * chosen for the new one immediately.
 *
 * A molecule shown with no protocol selected is a state this dialog must never
 * reach: the limits list below reads off the SELECTED protocol, so an empty
 * protocol field empties the card and leaves Confirm dead, with nothing on
 * screen explaining which of the two fields is at fault. Every molecule has at
 * least its baseline, so there is always something correct to fall back to.
 */
watch(selectedMoleculeId, (next, previous) => {
  if (previous === null || next === previous) return
  selectedProtocolId.value = defaultProtocolFor(molecules.value.find((m) => m.id === next))
})

/**
 * Open on what the country already resolves to.
 *
 * `profile.protocol` is the server's resolved answer for an unconfigured
 * account, which is precisely the proposal this dialog is asking about. Nothing
 * is written by pre-selecting it — the account stays unconfigured until Confirm.
 */
function syncFromProfile() {
  const resolved = profile.value
  if (!resolved?.known) return
  selectedMoleculeId.value = resolved.molecule?.id ?? null
  // `?? defaultProtocolFor` rather than `?? null`: the server resolves a
  // protocol for every account, but a molecule left showing an empty protocol
  // field would empty the limits list and disable Confirm with nothing on
  // screen to say why.
  selectedProtocolId.value =
    resolved.protocol?.id ?? defaultProtocolFor(selectedMolecule.value)
}

/**
 * The profile may already have been fetched by a dose validator before this
 * mounts, so sync on arrival as well as on load — otherwise a dialog opened by
 * somebody else's fetch would show two empty selects.
 */
watch(profile, syncFromProfile)

async function retryLoad() {
  try {
    await load({ force: true })
    syncFromProfile()
  } catch {
    // `error` is already set and rendered; the button becomes clickable again.
  }
}

async function confirm() {
  if (!selectedProtocolId.value) return
  saving.value = true
  try {
    // The protocol FIRST, always. The server refuses to layer an override onto
    // a protocol nobody selected, so a set written before the selection landed
    // would be rejected — or worse, pinned to the protocol being replaced.
    await save({
      moleculeId: selectedMoleculeId.value,
      protocolId: selectedProtocolId.value
    })

    const values = stagedOverrides.value
    if (Object.keys(values).length > 0) {
      try {
        await saveOverrides(values)
      } catch {
        // The protocol landed and the gate is down; only the limits did not.
        // Say which half failed rather than reporting the whole thing as a
        // failure — the adjustments can be re-made in Settings → Clinique,
        // and claiming nothing was saved would send the owner looking for a
        // protocol that is in fact already in force.
        showNotification(error.value || t('userAccount.clinical.adjust.saveFailed'), 'error')
        return
      }
    }

    // `save` re-reads the profile, and the write cleared the account's marker
    // server-side — so `setupRequired` goes false and the dialog closes itself.
    showNotification(t('userAccount.clinical.saved'), 'success')
  } catch {
    showNotification(error.value || t('userAccount.clinical.saveFailed'), 'error')
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  try {
    await load()
  } catch {
    // A load that never lands leaves `setupRequired` false, so no gate appears.
    // Deliberate: a blocking dialog is the wrong thing to guess at, and the
    // question is still waiting in Settings → Clinique.
    return
  }
  syncFromProfile()
})
</script>

<style scoped>
.setup-card {
  overflow: hidden;
}

/* The blue chrome, carried over from the portal's protocol panel. */
.setup-body {
  max-height: min(58vh, 560px);
  overflow-y: auto;
}

/* The pen holds its space at all times and only fades in, so the protocol name
   does not shift sideways as the pointer crosses the line. Revealed by keyboard
   focus too — an affordance that only exists under a mouse does not exist. */
/* Sits with the thing it edits, not hard right against the card edge: a pen a
   whole column away from the name reads as belonging to the row, not to the
   word. 2px on top of the flex gap makes 10px from the glyph. */
.identity-pen {
  opacity: 0;
  margin-left: 2px;
  transition: opacity 120ms ease;
}

.identity-line:hover .identity-pen,
.identity-pen:focus-visible {
  opacity: 1;
}

/* Each line keeps its height whether it is holding text or a select, so opening
   one does not push the limits below it up and down the card. */
.identity-line {
  min-height: 36px;
}

.identity-molecule {
  margin-top: 2px;
}

/* The select sits ON the line it replaces: no floating label, no helper row,
   nothing that makes the field taller than the text it stands in for. */
.identity-field {
  max-width: 100%;
}

.identity-field :deep(.v-field__input) {
  padding-top: 0;
  padding-bottom: 0;
  min-height: 28px;
  font-size: 0.9375rem;
}

/* On the bar, so they are the bar's ink: quiet against the title, full white
   under a pointer or a focus ring. Same pair, same corner, as the settings
   card's own header. */
.header-disclaimer,
.info-open {
  color: rgba(255, 255, 255, 0.78);
}

.header-disclaimer {
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: 0;
  text-transform: none;
}

.header-disclaimer:hover,
.header-disclaimer:focus-visible,
.info-open:hover,
.info-open:focus-visible {
  color: #fff;
}

/* Full-bleed rows: the separators run the width of the panel, as they do on the
   settings screen, so the list reads as the card's own content rather than as a
   box inside it. */
.setup-limits {
  list-style: none;
  margin: 8px -24px 0;
  padding: 0;
}

/* Same heading and same list as the settings card: a pharmacist reads this
   ladder twice, once here and once there, and it should not look like two
   different documents. */
.section-heading {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-on-surface), 0.55);
}

.ladder-list {
  padding-left: 20px;
}

.ladder-list li + li {
  margin-top: 6px;
}

.limit-row {
  padding-top: 7px;
  padding-bottom: 7px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.limit-value {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* A button that has to look exactly like the number it replaces — the
   affordance is the pointer and the row's own hover, not a control drawn
   around the value. Declared before `.limit-value-changed` so an adjusted
   number keeps its pill: equal specificity, later rule wins. */
.limit-value-btn {
  appearance: none;
  background: none;
  border: 0;
  padding: 0;
  color: inherit;
  cursor: pointer;
}

.limit-value-btn:not(.limit-value-changed):hover {
  text-decoration: underline;
  text-underline-offset: 3px;
}

/* A limit this account moved off the protocol's number. The row already says
   so underneath, but the number itself is what gets scanned, so it carries the
   flag: outlined rather than merely recoloured, because a bare colour change on
   one number in a column of numbers is not a difference anyone finds without
   being told to look. Blue on the light theme, yellow on the dark, where blue
   sits too close to the surface to read. */
.limit-value-changed {
  color: #2563eb;
  border: 1px solid currentColor;
  border-radius: 8px;
  padding: 1px 8px;
  /* The pill is drawn around the number, not around a taller line: without
     this the bordered rows would sit a pixel or two lower than the plain
     ones and the column of values would ripple. */
  margin: -1px 0;
}

.v-theme--customThemeDark .limit-value-changed {
  color: #ffd740;
}

.adjusted-count {
  color: rgba(0, 0, 0, 0.6);
}

.blocks-chip {
  background: rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  font-size: 11px;
  margin-left: 6px;
  padding: 1px 5px;
  white-space: nowrap;
}

/* The input stands where the value was, and no wider — a full-width field in a
   6-row list makes the column of numbers unreadable. */
.limit-field {
  flex: 0 0 132px;
  max-width: 132px;
}

.limit-field :deep(.v-field__input) {
  padding-top: 0;
  padding-bottom: 0;
  min-height: 28px;
  text-align: right;
}

.limit-toggle {
  flex: 0 0 auto;
}

.limit-toggle :deep(.v-selection-control) {
  min-height: 28px;
}

/* Same width the pen occupies, so rows that cannot be adjusted keep their
   numbers in the same column as rows that can. */
.pen-spacer {
  display: inline-block;
  width: 28px;
}

/* Same reveal as the identity pen, on its own class: the identity lines and the
   limit rows are queried separately, and one selector matching both makes "the
   pen" ambiguous in every test and every future style rule. */
.limit-pen {
  opacity: 0;
  transition: opacity 120ms ease;
}

.limit-row:hover .limit-pen,
.limit-pen:focus-visible {
  opacity: 1;
}

/* The row under the pointer — or holding the keyboard focus, which is the same
   state reached without one — takes the accent colour, label and value
   together. The pen fading in says something is possible here; the colour says
   WHICH row it would happen to, which a 14px glyph on its own does not. */
.limit-row:hover,
.limit-row:focus-within {
  color: #2563eb;
}

/* Both draw from `currentColor`, so they follow the row rather than keeping
   Vuetify's own default while the text around them changes. */
.limit-row:hover .limit-pen,
.limit-row:focus-within .limit-pen,
.limit-row:focus-within .identity-check {
  color: inherit;
}

/* The row hover recolours everything inside it, which would dissolve an
   adjusted value's flag into the hover state on exactly the rows a pharmacist
   is pointing at. It keeps its own colour. */
.limit-row:hover .limit-value-changed,
.limit-row:focus-within .limit-value-changed {
  color: #2563eb;
}

.v-theme--customThemeDark .limit-row:hover .limit-value-changed,
.v-theme--customThemeDark .limit-row:focus-within .limit-value-changed {
  color: #ffd740;
}

.setup-footer {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

/* Every action, outlined and solid alike, sizes to its own label and never
   shrinks or grows. Without this the longest translation of "Reset all to
   protocol" wraps inside its own pill, and Confirm (ml-auto pushes it to the
   row's right edge) would stretch to fill whatever space is left instead. */
.footer-action {
  flex: 0 0 auto;
}

.citation-card {
  max-width: 380px;
}
</style>
